import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Building, ChevronRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { AirlineSelectionModalForSignup } from '../components/modals/AirlineSelectionModalForSignup';

// =====================================================================
// Types
// =====================================================================

interface SelectedAirline {
  id: string;
  name: string;
  code: string;
}

// =====================================================================
// Validation Schema
// =====================================================================

const signupSchema = z.object({
  full_name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email address'),
  password: z.string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number'),
  confirmPassword: z.string(),
  role: z.enum(['instructor', 'trainee', 'admin'], {
    required_error: 'Please select a role',
  }),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ['confirmPassword'],
});

type SignupFormData = z.infer<typeof signupSchema>;

// =====================================================================
// Component
// =====================================================================

export default function Signup() {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Airline selection state
  const [showAirlineModal, setShowAirlineModal] = useState(false);
  const [selectedAirline, setSelectedAirline] = useState<SelectedAirline | null>(null);
  const [airlineError, setAirlineError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
  });

  // ===================================================================
  // Handlers
  // ===================================================================

  const handleAirlineSelect = (airline: SelectedAirline) => {
    setSelectedAirline(airline);
    setAirlineError(null);
    setShowAirlineModal(false);
  };

  const onSubmit = async (data: SignupFormData) => {
    // Validate airline selection
    if (!selectedAirline) {
      setAirlineError('Please select an airline');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Step 1: Create auth user (this triggers handle_new_user which creates the profile)
      const { error: signUpError } = await signUp(data.email, data.password, {
        role: data.role,
        full_name: data.full_name,
        requested_airline_id: selectedAirline.id,
      });

      if (signUpError) {
        setError(signUpError.message);
        setIsLoading(false);
        return;
      }

      setSuccess(true);
      // Redirect to login after showing success message
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      console.error('[Signup] Unexpected error:', err);
      setError('An unexpected error occurred. Please try again.');
      setIsLoading(false);
    }
  };

  // ===================================================================
  // Success Screen
  // ===================================================================

  if (success) {
    return (
      <div className="min-h-screen flex items-start justify-center lg:justify-end pt-[8vh] pb-6 px-4 lg:pr-[8%] auth-bg">
        <div className="fixed inset-0 bg-navy/70" aria-hidden="true" />
        <div className="relative max-w-md w-full space-y-4">
          {/* Logo on success screen */}
          <div className="flex justify-center">
            <img
              src="/images/wingmentor-logo.png"
              alt="WingMentor Logo"
              className="h-16 w-auto opacity-70 hover:opacity-100 transition-opacity duration-300"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                if (fallback) fallback.style.display = 'block';
              }}
            />
            <div style={{ display: 'none' }}>
              <h1 className="text-3xl font-heading font-bold text-white mb-1">WingMentor</h1>
              <p className="text-white/80 text-sm tracking-wide">COMPETENCY ASSESSMENT</p>
            </div>
          </div>

          {/* Success message */}
          <div className="rounded-xl border-2 border-orca-elevate/35 bg-white/80 px-7 py-5 text-center shadow-2xl backdrop-blur-md">
            <div className="text-orca-elevate text-5xl mb-3">&#10003;</div>
            <h2 className="text-xl font-heading font-semibold text-navy mb-2">Account Created!</h2>
            <p className="text-steel text-sm mb-3">Please check your email to verify your account.</p>

            {/* Pending approval notice */}
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> After verifying your email, an administrator will review your
                request to join <strong>{selectedAirline?.name}</strong>. You'll receive an email
                once your account is approved.
              </p>
            </div>

            <div className="mt-4 p-2.5 bg-orca-elevate/10 rounded-lg">
              <p className="text-sm text-navy font-medium">Redirecting to login...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ===================================================================
  // Signup Form
  // ===================================================================

  return (
    <div className="min-h-screen flex items-start justify-center lg:justify-end pt-[5vh] pb-6 px-4 sm:px-6 lg:pr-[8%] auth-bg">
      <div className="fixed inset-0 bg-navy/70" aria-hidden="true" />
      <div className="relative max-w-md w-full space-y-3">
        {/* Header with Logo */}
        <div className="text-center">
          <div className="flex justify-center mb-3">
            <img
              src="/images/wingmentor-logo.png"
              alt="WingMentor Logo"
              className="h-16 w-auto opacity-70 hover:opacity-100 transition-opacity duration-300"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                if (fallback) fallback.style.display = 'block';
              }}
            />
            <div style={{ display: 'none' }}>
              <h1 className="text-3xl font-heading font-bold text-white mb-1">WingMentor</h1>
              <p className="text-white/80 text-sm tracking-wide">COMPETENCY ASSESSMENT</p>
            </div>
          </div>
          <h2 className="text-xl font-heading font-semibold text-white mb-1">Join WingMentor</h2>
          <p className="text-white/80 text-sm">Create your account to get started</p>
        </div>

        {/* Form */}
        <form
          className="space-y-3 rounded-xl border-2 border-orca-elevate/15 bg-white/80 px-7 py-5 shadow-2xl backdrop-blur-md ring-1 ring-white/30"
          onSubmit={handleSubmit(onSubmit)}
        >
          {error && (
            <div className="bg-signal/10 border-l-4 border-signal text-navy px-4 py-2 rounded">
              <p className="font-medium text-sm">Error</p>
              <p className="text-sm">{error}</p>
            </div>
          )}

          <div className="space-y-2.5">
            {/* Full Name */}
            <div>
              <label htmlFor="full_name" className="block text-sm font-medium text-navy mb-0.5">
                Full Name
              </label>
              <input
                id="full_name"
                type="text"
                autoComplete="name"
                {...register('full_name')}
                className="appearance-none relative block w-full px-3 py-1.5 bg-white border border-steel/30 rounded-md placeholder-steel/50 text-navy focus:outline-none focus:ring-2 focus:ring-orca-elevate focus:border-orca-elevate sm:text-sm"
                placeholder="John Doe"
              />
              {errors.full_name && (
                <p className="mt-0.5 text-xs text-red-600">{errors.full_name.message}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-navy mb-0.5">
                Email address
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                {...register('email')}
                className="appearance-none relative block w-full px-3 py-1.5 bg-white border border-steel/30 rounded-md placeholder-steel/50 text-navy focus:outline-none focus:ring-2 focus:ring-orca-elevate focus:border-orca-elevate sm:text-sm"
                placeholder="you@example.com"
              />
              {errors.email && (
                <p className="mt-0.5 text-xs text-red-600">{errors.email.message}</p>
              )}
            </div>

            {/* Role Selection */}
            <div>
              <label htmlFor="role" className="block text-sm font-medium text-navy mb-0.5">
                Role
              </label>
              <select
                id="role"
                {...register('role')}
                className="appearance-none relative block w-full px-3 py-1.5 bg-white border border-steel/30 rounded-md placeholder-steel/50 text-navy focus:outline-none focus:ring-2 focus:ring-orca-elevate focus:border-orca-elevate sm:text-sm"
              >
                <option value="">Select your role...</option>
                <option value="instructor">Instructor</option>
                <option value="trainee">Trainee</option>
                <option value="admin">Administrator</option>
              </select>
              {errors.role && (
                <p className="mt-0.5 text-xs text-red-600">{errors.role.message}</p>
              )}
            </div>

            {/* Airline Selection */}
            <div>
              <label className="block text-sm font-medium text-navy mb-0.5">
                Airline <span className="text-red-500">*</span>
              </label>
              <button
                type="button"
                onClick={() => setShowAirlineModal(true)}
                className={`w-full flex items-center justify-between px-3 py-1.5 bg-white border rounded-md text-left text-sm transition-colors ${
                  airlineError
                    ? 'border-red-500 focus:ring-red-500'
                    : selectedAirline
                    ? 'border-orca-elevate bg-orca-elevate/5'
                    : 'border-steel/30 hover:border-steel/50'
                } focus:outline-none focus:ring-2 focus:ring-orca-elevate`}
              >
                <div className="flex items-center gap-2">
                  <Building size={16} className={selectedAirline ? 'text-orca-elevate' : 'text-steel'} />
                  {selectedAirline ? (
                    <span className="text-navy font-medium">
                      {selectedAirline.name}
                      <span className="text-steel font-normal ml-2">({selectedAirline.code})</span>
                    </span>
                  ) : (
                    <span className="text-steel/50">Select your airline...</span>
                  )}
                </div>
                <ChevronRight size={16} className="text-steel" />
              </button>
              {airlineError && (
                <p className="mt-0.5 text-xs text-red-600">{airlineError}</p>
              )}
              <p className="mt-0.5 text-xs text-steel/70">
                Your request will be reviewed by an administrator
              </p>
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-navy mb-0.5">
                Password
              </label>
              <input
                id="password"
                type="password"
                autoComplete="new-password"
                {...register('password')}
                className="appearance-none relative block w-full px-3 py-1.5 bg-white border border-steel/30 rounded-md placeholder-steel/50 text-navy focus:outline-none focus:ring-2 focus:ring-orca-elevate focus:border-orca-elevate sm:text-sm"
                placeholder="••••••••"
              />
              {errors.password && (
                <p className="mt-0.5 text-xs text-red-600">{errors.password.message}</p>
              )}
              <p className="mt-0.5 text-xs text-steel/70">
                Must contain uppercase, lowercase, and numbers
              </p>
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-navy mb-0.5">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                autoComplete="new-password"
                {...register('confirmPassword')}
                className="appearance-none relative block w-full px-3 py-1.5 bg-white border border-steel/30 rounded-md placeholder-steel/50 text-navy focus:outline-none focus:ring-2 focus:ring-orca-elevate focus:border-orca-elevate sm:text-sm"
                placeholder="••••••••"
              />
              {errors.confirmPassword && (
                <p className="mt-0.5 text-xs text-red-600">{errors.confirmPassword.message}</p>
              )}
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Creating account...' : 'Create account'}
          </button>

          {/* Login link */}
          <div className="text-center text-sm">
            <span className="text-steel">Already have an account? </span>
            <Link to="/login" className="font-medium text-orca-elevate hover:text-orca-elevate/80">
              Sign in
            </Link>
          </div>
        </form>
      </div>

      {/* Airline Selection Modal */}
      {showAirlineModal && (
        <AirlineSelectionModalForSignup
          onSelect={handleAirlineSelect}
          onClose={() => setShowAirlineModal(false)}
          selectedAirlineId={selectedAirline?.id}
        />
      )}
    </div>
  );
}
